import moment from "moment";
import { recomputeScopedPlayerScores, recomputeEntryPts } from "./kpiPoints";
import { countsTowardAverage } from "./scoreAggregate";

/** Previous calendar month (company-local moment at month start). */
export const lastMonthMoment = () => moment().subtract(1, "month").startOf("month");

export const lastMonthKey = () => lastMonthMoment().format("YYYY-MM");

/**
 * Closed-book recompute — matches Live TV / Past Boards for lastMonth:
 * amounts stay as rolled up; points are derived from KPI rules (not summed
 * raw daily pts, which inflates totals on money KPIs).
 */
export const recomputeClosedMonth = (rawScores, scoringKpis) =>
  recomputeScopedPlayerScores(rawScores, scoringKpis, {
    recomputeAnchorAsProgressive: true,
  });

/**
 * Fix per-user totals after daily score folding (readWorkspace / sumScores).
 * Mutates `perUser` entries in place: { kpis, pts, total? }.
 */
export const applyClosedMonthRecompute = (perUser, scoringKpis) => {
  if (!perUser || !scoringKpis?.length) return;
  Object.values(perUser).forEach((u) => {
    const raw = { ...(u.kpis || {}) };
    const scoped = recomputeClosedMonth(raw, scoringKpis);
    u.kpis = scoped;
    const total = Number(scoped.total) || 0;
    u.pts = total;
    if (u.total != null) u.total = total;
  });
};

/** Recompute one KPI cell's points for team-average tiles. */
export const recomputeKpiCellPts = (cell, kpiDef) =>
  recomputeEntryPts(cell, kpiDef);

/** Rebuild readWorkspace-style byKpi averages from recomputed player cells. */
export const byKpiFromPlayers = (players, metaById = null) => {
  const byKpi = {};
  (players || []).forEach((p) => {
    Object.entries(p.kpis || {}).forEach(([kpiId, cell]) => {
      if (kpiId === "total" || !cell) return;
      if (metaById && !countsTowardAverage(cell)) return;
      if (!byKpi[kpiId]) byKpi[kpiId] = { sum: 0, pts: 0, scorers: 0 };
      byKpi[kpiId].sum += Number(cell.amt) || 0;
      byKpi[kpiId].pts += Number(cell.pts) || 0;
      byKpi[kpiId].scorers += 1;
    });
  });
  Object.values(byKpi).forEach((o) => {
    o.avg = o.scorers ? o.sum / o.scorers : 0;
    o.avgPts = o.scorers ? o.pts / o.scorers : 0;
  });
  return byKpi;
};

/**
 * KPI defs needed to recompute a closed month — workspace catalog plus any
 * retired/disabled metrics that still appear in folded daily scores (same idea
 * as Past Boards / Live TV lastMonth).
 */
export const scoringKpisForHistoricSlice = (slice, defsAll, groupAllKpis) => {
  const byId = {};
  (defsAll || []).forEach((k) => {
    if (k?._id) byId[String(k._id)] = k;
  });
  (groupAllKpis || []).forEach((k) => {
    const id = k?._id ? String(k._id) : null;
    if (id && !byId[id]) byId[id] = k;
  });
  const seen = new Set();
  (slice?.players || []).forEach((p) => {
    Object.keys(p.kpis || {}).forEach((id) => {
      if (id !== "total") seen.add(String(id));
    });
  });
  const fromScores = [...seen].map((id) => byId[id]).filter(Boolean);
  return fromScores.length ? fromScores : (defsAll || []);
};

/**
 * Build a scoped-metrics slice from Redux historic month rows — the same
 * source Live TV / UseTopPlayers use, so KOTM and Yeti tiles match the board.
 */
export const workspaceSliceFromHistoric = (historicRows, scoringKpis, metaById = null) => {
  if (!Array.isArray(historicRows) || !scoringKpis?.length) {
    return { byKpi: {}, players: [], teamPts: 0 };
  }
  const players = historicRows
    .filter((row) => row?.month)
    .map((row) => {
      const scoped = recomputeClosedMonth(row.month, scoringKpis);
      return {
        userId: row.userId,
        pts: Number(scoped.total) || 0,
        kpis: scoped,
      };
    })
    .filter((p) => p.pts > 0);
  const teamPts = players.reduce((n, p) => n + (Number(p.pts) || 0), 0);
  return {
    players,
    teamPts,
    byKpi: byKpiFromPlayers(players, metaById),
  };
};

/** After readWorkspace for a closed month, recompute player pts + rebuild byKpi. */
export const finalizeClosedMonthSlice = (slice, scoringKpis, metaById = null) => {
  if (!slice?.players?.length || !scoringKpis?.length) return slice;
  const byId = {};
  slice.players.forEach((p) => {
    byId[p.userId] = { userId: p.userId, pts: p.pts, kpis: { ...(p.kpis || {}) } };
  });
  applyClosedMonthRecompute(byId, scoringKpis);
  const players = Object.values(byId).map((p) => ({
    userId: p.userId,
    pts: p.pts,
    kpis: p.kpis,
  }));
  return {
    ...slice,
    players,
    teamPts: players.reduce((n, p) => n + (Number(p.pts) || 0), 0),
    byKpi: byKpiFromPlayers(players, metaById),
  };
};
